import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AppLayout } from '@/widgets/layout/AppLayout';
import { LmsLayout } from '@/widgets/layout/LmsLayout';
import { AuthGuard } from './AuthGuard';
import { EmailVerificationGuard } from './EmailVerificationGuard';
import { ConsentGuard } from './ConsentGuard';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import { RequireLmsRole } from '@/shared/lib/lms/RequireLmsRole';
import { CookieConsentBanner } from '@/widgets/legal/CookieConsentBanner';

const ProfilePage = lazy(() => import('@/pages/profile/ui/ProfilePage'));
const ProfileEditPage = lazy(() => import('@/pages/profile/ui/ProfileEditPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/ui/DashboardPage'));
const OrganizationsPage = lazy(() => import('@/pages/organizations/ui/OrganizationsPage'));
const OrganizationDetailPage = lazy(
  () => import('@/pages/organizations/ui/OrganizationDetailPage'),
);
const OrganizationInvitePage = lazy(
  () => import('@/pages/organizations/ui/OrganizationInvitePage'),
);
const ActivityPage = lazy(() => import('@/pages/activity/ui/ActivityPage'));
const ActivityHistoryPage = lazy(() => import('@/pages/activity/ui/ActivityHistoryPage'));
const ArchivePage = lazy(() => import('@/pages/activity/ui/ArchivePage'));
const BillingPage = lazy(() => import('@/pages/billing/ui/BillingPage'));
const BillingPlansPage = lazy(() => import('@/pages/billing/ui/BillingPlansPage'));
const BillingPaymentsPage = lazy(() => import('@/pages/billing/ui/BillingPaymentsPage'));
const BillingMethodsPage = lazy(() => import('@/pages/billing/ui/BillingMethodsPage'));
const InterfaceSettingsPage = lazy(() => import('@/pages/settings/ui/InterfaceSettingsPage'));
const SecuritySettingsPage = lazy(() => import('@/pages/settings/ui/SecuritySettingsPage'));
const NotificationsSettingsPage = lazy(
  () => import('@/pages/settings/ui/NotificationsSettingsPage'),
);
const PrivacySettingsPage = lazy(() => import('@/pages/settings/ui/PrivacySettingsPage'));
const ApiKeysSettingsPage = lazy(() => import('@/pages/settings/ui/ApiKeysSettingsPage'));
const ReferralPage = lazy(() => import('@/pages/referral/ui/ReferralPage'));
const AdminRolesPage = lazy(() => import('@/pages/admin/ui/AdminRolesPage'));
const AdminUsersPage = lazy(() => import('@/pages/admin/ui/AdminUsersPage'));
const AdminServicesPage = lazy(() => import('@/pages/admin/ui/AdminServicesPage'));
const LoginPage = lazy(() => import('@/pages/auth/ui/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/ui/RegisterPage'));
const ConsentsPage = lazy(() => import('@/pages/auth/ui/ConsentsPage'));
const OAuthCallbackPage = lazy(() => import('@/pages/auth/ui/OAuthCallbackPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ui/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ui/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/ui/VerifyEmailPage'));
const NotFoundPage = lazy(() => import('@/pages/not-found/ui/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/pages/not-found/ui/ForbiddenPage'));

// LMS pages
const LmsRoleHomePage = lazy(() => import('@/pages/lms/home/RoleHomePage'));
const LmsCatalogPage = lazy(() => import('@/pages/lms/catalog/CatalogPage'));
const LmsCoursePage = lazy(() => import('@/pages/lms/catalog/CoursePage'));
const LmsProgramPage = lazy(() => import('@/pages/lms/catalog/ProgramPage'));
const LmsCheckoutPage = lazy(() => import('@/pages/lms/checkout/CheckoutPage'));
const LmsSuccessPage = lazy(() => import('@/pages/lms/checkout/SuccessPage'));
const LmsMyCoursesPage = lazy(() => import('@/pages/lms/learning/MyCoursesPage'));
const LmsProgramStudyPage = lazy(() => import('@/pages/lms/learning/ProgramStudyPage'));
const LmsLessonPage = lazy(() => import('@/pages/lms/learning/LessonPage'));
const LmsTestPage = lazy(() => import('@/pages/lms/learning/TestPage'));
const LmsAssignmentPage = lazy(() => import('@/pages/lms/learning/AssignmentPage'));
const LmsProjectPage = lazy(() => import('@/pages/lms/learning/ProjectPage'));
const LmsNotificationsPage = lazy(() => import('@/pages/lms/notifications/NotificationsPage'));
const LmsTeamsBoardPage = lazy(() => import('@/pages/lms/teams/TeamsBoardPage'));
const LmsTeamPage = lazy(() => import('@/pages/lms/teams/TeamPage'));
const LmsTeachQueuePage = lazy(() => import('@/pages/lms/teach/TeachQueuePage'));
const LmsCurateGroupsPage = lazy(() => import('@/pages/lms/curate/CurateGroupsPage'));
const LmsCurateGroupPage = lazy(() => import('@/pages/lms/curate/CurateGroupPage'));
const LmsAnalyticsPage = lazy(() => import('@/pages/lms/analytics/AnalyticsPage'));
const LmsAdminOverviewPage = lazy(() => import('@/pages/lms/admin/AdminOverviewPage'));
const LmsAdminPromoPage = lazy(() => import('@/pages/lms/admin/AdminPromoPage'));
const LmsAdminVacanciesPage = lazy(() => import('@/pages/lms/admin/AdminVacanciesPage'));
const LmsAdminSchedulePage = lazy(() => import('@/pages/lms/admin/AdminSchedulePage'));
const LmsAdminAuditPage = lazy(() => import('@/pages/lms/admin/AdminAuditPage'));
const LmsAdminOrdersPage = lazy(() => import('@/pages/lms/admin/AdminOrdersPage'));
const LmsAdminPaymentsPage = lazy(() => import('@/pages/lms/admin/AdminPaymentsPage'));
const LmsAdminTariffsPage = lazy(() => import('@/pages/lms/admin/AdminTariffsPage'));
const LmsAdminCertificatesPage = lazy(() => import('@/pages/lms/admin/AdminCertificatesPage'));
const LmsAdminUsersPage = lazy(() => import('@/pages/lms/admin/AdminUsersPage'));
const LmsAdminProgramsPage = lazy(() => import('@/pages/lms/admin/AdminProgramsPage'));
const LmsAdminCoursesPage = lazy(() => import('@/pages/lms/admin/AdminCoursesPage'));
const LmsAdminCourseEditorPage = lazy(() => import('@/pages/lms/admin/AdminCourseEditorPage'));
const LmsAdminLessonEditorPage = lazy(() => import('@/pages/lms/admin/AdminLessonEditorPage'));
const LmsAdminEnrollmentsPage = lazy(() => import('@/pages/lms/admin/AdminEnrollmentsPage'));

export function AppRouter() {
  return (
    <BrowserRouter>
      <CookieConsentBanner />
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />

          <Route element={<AuthGuard />}>
            <Route element={<ConsentGuard />}>
              <Route path="/app/consents" element={<ConsentsPage />} />
              <Route element={<EmailVerificationGuard />}>
              <Route path="/app/verify-email" element={<VerifyEmailPage />} />
              <Route path="/app/verify-required" element={<VerifyEmailPage />} />
              <Route path="/app" element={<AppLayout />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />

              {/* UC_002 — Profile */}
              <Route path="profile" element={<ProfilePage />} />
              <Route path="profile/edit" element={<ProfileEditPage />} />

              {/* UC_003 / UC_004 — Services hub, SCRIBER, Projects: hidden for all users */}
              <Route path="services/*" element={<Navigate to="/app/dashboard" replace />} />
              <Route path="projects/*" element={<Navigate to="/app/dashboard" replace />} />

              {/* UC_004 — Organizations */}
              <Route path="organizations" element={<OrganizationsPage />} />
              <Route path="organizations/invite" element={<OrganizationInvitePage />} />
              <Route path="organizations/:id" element={<OrganizationDetailPage />} />

              {/* UC_005 — Activity */}
              <Route path="activity" element={<ActivityPage />} />
              <Route path="activity/history" element={<ActivityHistoryPage />} />
              <Route path="archive" element={<ArchivePage />} />

              {/* UC_006 — Billing */}
              <Route path="billing" element={<BillingPage />} />
              <Route path="billing/plans" element={<BillingPlansPage />} />
              <Route path="billing/payments" element={<BillingPaymentsPage />} />
              <Route path="billing/methods" element={<BillingMethodsPage />} />

              {/* UC_009 — Settings */}
              <Route path="settings" element={<Navigate to="/app/settings/interface" replace />} />
              <Route path="settings/interface" element={<InterfaceSettingsPage />} />
              <Route path="settings/security" element={<SecuritySettingsPage />} />
              <Route path="settings/notifications" element={<NotificationsSettingsPage />} />
              <Route path="settings/privacy" element={<PrivacySettingsPage />} />
              <Route path="settings/api-keys" element={<ApiKeysSettingsPage />} />

              {/* UC_010 — Referral */}
              <Route path="referral" element={<ReferralPage />} />

              {/* UC_007 — Admin */}
              <Route path="admin/users" element={<AdminUsersPage />} />
              <Route path="admin/services" element={<AdminServicesPage />} />
              <Route path="admin/roles" element={<AdminRolesPage />} />
              </Route>

              {/* LMS — UC_011..028 */}
              <Route path="/lms" element={<LmsLayout />}>
                <Route index element={<Navigate to="/lms/home" replace />} />
                <Route path="home" element={<LmsRoleHomePage />} />
                <Route path="catalog" element={<LmsCatalogPage />} />
                <Route path="courses/:slug" element={<LmsCoursePage />} />
                <Route path="courses/:slug/teams" element={<LmsTeamsBoardPage />} />
                <Route path="teams/:teamId" element={<LmsTeamPage />} />
                <Route path="programs/:slug" element={<LmsProgramPage />} />
                <Route path="checkout/success" element={<LmsSuccessPage />} />
                <Route path="checkout/:orderId" element={<LmsCheckoutPage />} />
                <Route path="my" element={<LmsMyCoursesPage />} />
                <Route path="study/programs/:slug" element={<LmsProgramStudyPage />} />
                <Route path="lesson/:lessonId" element={<LmsLessonPage />} />
                <Route path="test/:testId" element={<LmsTestPage />} />
                <Route path="assignment/:assignmentId" element={<LmsAssignmentPage />} />
                <Route path="projects/:gid/:aid" element={<LmsProjectPage />} />
                <Route path="notifications" element={<LmsNotificationsPage />} />

                <Route
                  path="teach"
                  element={
                    <RequireLmsRole roles={['teacher', 'methodist']}>
                      <LmsTeachQueuePage />
                    </RequireLmsRole>
                  }
                />

                <Route path="curate" element={<Navigate to="/lms/curate/groups" replace />} />
                <Route
                  path="curate/groups"
                  element={
                    <RequireLmsRole roles={['curator']}>
                      <LmsCurateGroupsPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="curate/groups/:gid"
                  element={
                    <RequireLmsRole roles={['curator']}>
                      <LmsCurateGroupPage />
                    </RequireLmsRole>
                  }
                />

                <Route
                  path="analytics"
                  element={
                    <RequireLmsRole roles={['methodist']}>
                      <LmsAnalyticsPage />
                    </RequireLmsRole>
                  }
                />

                <Route
                  path="admin"
                  element={
                    <RequireLmsRole roles={['methodist', 'admin_lms']}>
                      <LmsAdminOverviewPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/promo"
                  element={
                    <RequireLmsRole roles={['admin_lms']}>
                      <LmsAdminPromoPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/vacancies"
                  element={
                    <RequireLmsRole roles={['methodist', 'admin_lms']}>
                      <LmsAdminVacanciesPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/schedule"
                  element={
                    <RequireLmsRole roles={['methodist', 'admin_lms']}>
                      <LmsAdminSchedulePage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/audit"
                  element={
                    <RequireLmsRole roles={['admin_lms']}>
                      <LmsAdminAuditPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/orders"
                  element={
                    <RequireLmsRole roles={['admin_lms']}>
                      <LmsAdminOrdersPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/payments"
                  element={
                    <RequireLmsRole roles={['admin_lms']}>
                      <LmsAdminPaymentsPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/tariffs"
                  element={
                    <RequireLmsRole roles={['methodist', 'admin_lms']}>
                      <LmsAdminTariffsPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/certificates"
                  element={
                    <RequireLmsRole roles={['admin_lms']}>
                      <LmsAdminCertificatesPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/users"
                  element={
                    <RequireLmsRole roles={['admin_lms']}>
                      <LmsAdminUsersPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/programs"
                  element={
                    <RequireLmsRole roles={['methodist', 'admin_lms']}>
                      <LmsAdminProgramsPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/courses"
                  element={
                    <RequireLmsRole roles={['methodist', 'admin_lms']}>
                      <LmsAdminCoursesPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/courses/:courseId"
                  element={
                    <RequireLmsRole roles={['methodist', 'admin_lms']}>
                      <LmsAdminCourseEditorPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/lessons/:lessonId"
                  element={
                    <RequireLmsRole roles={['teacher', 'methodist', 'admin_lms']}>
                      <LmsAdminLessonEditorPage />
                    </RequireLmsRole>
                  }
                />
                <Route
                  path="admin/enrollments"
                  element={
                    <RequireLmsRole roles={['admin_lms']}>
                      <LmsAdminEnrollmentsPage />
                    </RequireLmsRole>
                  }
                />
              </Route>
              </Route>
            </Route>
          </Route>

          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="/" element={<Navigate to="/app" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
